import React, { useEffect, useMemo, useRef, useState } from "react";
import { CircleMarker, MapContainer, Polyline, TileLayer, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

const PUBLIC_TRACKING_API =
  "https://asia-east1-chat3test-59045.cloudfunctions.net/publicTracking";
const TALLARGE_LOGO_URL =
  "https://tallarge.com/static/media/Tallarge_final.43c62f7bd14af576d68e.png";

function text(value) {
  return String(value ?? "").trim();
}

function readTokenFromLocation() {
  const params = new URLSearchParams(window.location.search);
  const token = text(params.get("token"));
  console.info("[PublicTrackingPage] incoming tracking URL", {
    pathname: window.location.pathname,
    tokenKeyPresent: params.has("token"),
    tokenValuePresent: Boolean(token),
    tokenLength: token.length,
    queryKeys: Array.from(params.keys()),
  });
  return token;
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return `${date.getFullYear()}年${String(date.getMonth() + 1).padStart(2, "0")}月${String(date.getDate()).padStart(2, "0")}日`;
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("zh-TW", {
    timeZone: "Asia/Taipei",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);
}

function formatClockTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("zh-TW", {
    timeZone: "Asia/Taipei",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);
}

function dateKey(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return text(value).slice(0, 10);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function dateKeyFromParts(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function deliveryDateKey(delivery) {
  return text(delivery?.dateKey) || dateKey(delivery?.startAt);
}

function calendarMonthFromDate(value) {
  const key = dateKey(value) || dateKey(new Date());
  const [year, month] = key.split("-").map(Number);
  return { year: year || new Date().getFullYear(), month: (month || 1) - 1 };
}

function calendarDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  return Array.from({ length: 42 }, (_, index) => {
    const day = index - firstDay + 1;
    if (day < 1 || day > daysInMonth) return null;
    return { day, key: dateKeyFromParts(year, month, day) };
  });
}

function formatCalendarMonth(year, month) {
  return `${year} 年 ${String(month + 1).padStart(2, "0")} 月`;
}

function statusLabel(value) {
  const status = text(value).toUpperCase();
  return {
    PENDING_PICKUP: "待取貨",
    PICKED_UP: "已取貨",
    IN_TRANSIT: "運送中",
    DELIVERED: "已送達",
    EXCEPTION: "異常",
    ASSIGNED: "待取貨",
    PLANNED: "尚未開始",
    IN_PROGRESS: "進行中",
    APPROACHING: "即將抵達",
    ARRIVED: "已抵達",
    ACTIVE: "配送中",
    BUSY: "配送中",
    PAUSED: "暫停中",
    COMPLETED: "已完成",
    DONE: "已完成",
    CANCELLED: "已取消",
    CANCELED: "已取消",
    FAILED: "異常",
    PENDING: "待出發",
  }[status] || "配送中";
}

function statusTone(value) {
  const status = text(value).toUpperCase();
  if (["DELIVERED", "COMPLETED", "DONE"].includes(status)) return "green";
  if (["EXCEPTION", "FAILED", "CANCELLED", "CANCELED"].includes(status)) return "red";
  if (["IN_TRANSIT", "PICKED_UP", "IN_PROGRESS", "ACTIVE", "APPROACHING", "ARRIVED", "BUSY"].includes(status)) return "orange";
  return "blue";
}

function stationEvidenceKey(delivery, stop) {
  const applicationId = text(delivery?.applicationId);
  const stopId = text(stop?.id || stop?.stopId).toUpperCase();
  return `${deliveryDateKey(delivery)}|${applicationId}|${stopId}`;
}

const ROUTE_START_COLOR = [24, 185, 119];
const ROUTE_END_COLOR = [239, 83, 96];

function routeGradientColor(progress) {
  const amount = Math.max(0, Math.min(1, Number(progress) || 0));
  const channels = ROUTE_START_COLOR.map((start, index) =>
    Math.round(start + ((ROUTE_END_COLOR[index] - start) * amount)),
  );
  return `rgb(${channels.join(",")})`;
}

function buildRouteGradientSegments(positions) {
  const edgeCount = positions.length - 1;
  if (edgeCount < 1) return [];
  const subdivisionsPerEdge = Math.max(1, Math.ceil(24 / edgeCount));
  const segments = [];
  for (let edgeIndex = 0; edgeIndex < edgeCount; edgeIndex += 1) {
    const start = positions[edgeIndex];
    const end = positions[edgeIndex + 1];
    for (let part = 0; part < subdivisionsPerEdge; part += 1) {
      const from = part / subdivisionsPerEdge;
      const to = (part + 1) / subdivisionsPerEdge;
      const pointAt = (progress) => [
        start[0] + ((end[0] - start[0]) * progress),
        start[1] + ((end[1] - start[1]) * progress),
      ];
      const routeProgress = (edgeIndex + ((part + 0.5) / subdivisionsPerEdge)) / edgeCount;
      segments.push({
        positions: [pointAt(from), pointAt(to)],
        color: routeGradientColor(routeProgress),
      });
    }
  }
  return segments;
}

function RouteMapAutoFit({ positions }) {
  const map = useMap();
  useEffect(() => {
    const fitRoute = () => {
      map.invalidateSize({ pan: false });
      map.fitBounds(positions, { padding: [28, 28], maxZoom: 17, animate: false });
    };
    fitRoute();
    window.addEventListener("beforeprint", fitRoute);
    window.addEventListener("afterprint", fitRoute);
    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(fitRoute);
      resizeObserver.observe(map.getContainer());
    }
    return () => {
      window.removeEventListener("beforeprint", fitRoute);
      window.removeEventListener("afterprint", fitRoute);
      resizeObserver?.disconnect();
    };
  }, [map, positions]);
  return null;
}

function PublicRouteMap({ points }) {
  const positions = useMemo(() => (Array.isArray(points) ? points : [])
    .filter((point) => Number.isFinite(Number(point?.lat)) && Number.isFinite(Number(point?.lng)))
    .slice()
    .sort((left, right) => {
      const leftAt = Number(left?.observedAt);
      const rightAt = Number(right?.observedAt);
      return Number.isFinite(leftAt) && Number.isFinite(rightAt) ? leftAt - rightAt : 0;
    })
    .map((point) => [Number(point.lat), Number(point.lng)]), [points]);
  const gradientSegments = useMemo(() => buildRouteGradientSegments(positions), [positions]);
  if (positions.length < 2) return null;
  return (
    <div className="pt-evidence-map" role="img" aria-label="本站作業期間的 GPS 路線">
      <MapContainer bounds={positions} boundsOptions={{ padding: [22, 22] }} scrollWheelZoom={false} style={{ width: "100%", height: "100%" }}>
        <RouteMapAutoFit positions={positions} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {gradientSegments.map((segment, index) => (
          <Polyline key={`route-gradient-${index}`} positions={segment.positions} pathOptions={{ color: segment.color, weight: 6, opacity: 0.92, lineCap: "round", lineJoin: "round" }} />
        ))}
        <CircleMarker center={positions[0]} radius={7} pathOptions={{ color: "#ffffff", weight: 2, fillColor: "#18b977", fillOpacity: 1 }}>
          <Tooltip>出發</Tooltip>
        </CircleMarker>
        <CircleMarker center={positions[positions.length - 1]} radius={8} pathOptions={{ color: "#ffffff", weight: 2, fillColor: "#ef5360", fillOpacity: 1 }}>
          <Tooltip>終點・本站</Tooltip>
        </CircleMarker>
      </MapContainer>
      <div className="pt-map-legend" aria-label="路線方向：綠色為出發，紅色為本站">
        <span><i className="is-start" />出發</span>
        <b aria-hidden="true" />
        <span><i className="is-end" />本站</span>
      </div>
    </div>
  );
}

function PublicIcon({ name, size = 20 }) {
  const paths = {
    calendar: "M7 3v3M17 3v3M4 9h16M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1ZM8 13h.01M12 13h.01M16 13h.01M8 17h.01M12 17h.01",
    pin: "M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0ZM15 10a3 3 0 1 1-6 0 3 3 0 0 1 6 0",
    box: "M4 7 12 3l8 4v10l-8 4-8-4V7ZM4 7l8 4 8-4M12 11v10",
    arrow: "m8 5 7 7-7 7",
    lock: "M7 10V8a5 5 0 0 1 10 0v2M6 10h12v10H6z",
    mail: "M4 6h16v12H4zM4 7l8 6 8-6",
    check: "m5 12 4 4L19 6",
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={paths[name] || paths.box} />
    </svg>
  );
}

async function readJson(response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.ok) {
    console.warn("[PublicTrackingPage] tracking API response error", {
      httpStatus: response.status,
      code: text(payload.code),
      message: text(payload.message),
    });
    throw new Error(payload.message || "目前無法取得配送資訊。");
  }
  return payload;
}

async function loadTracking(token, sessionToken = "") {
  const params = new URLSearchParams({ token });
  if (sessionToken) params.set("session", sessionToken);
  return readJson(await fetch(`${PUBLIC_TRACKING_API}?${params.toString()}`));
}

function trackingSessionStorageKey(token) {
  return `tallargeTrackingSession:${token}`;
}

function readSavedTrackingSession(token) {
  try {
    const raw = sessionStorage.getItem(trackingSessionStorageKey(token));
    const saved = raw ? JSON.parse(raw) : null;
    if (!saved?.sessionToken || !saved?.expiresAt || new Date(saved.expiresAt).getTime() <= Date.now()) {
      sessionStorage.removeItem(trackingSessionStorageKey(token));
      return null;
    }
    return saved;
  } catch (error) {
    return null;
  }
}

function saveTrackingSession(token, sessionToken, expiresAt) {
  try {
    sessionStorage.setItem(trackingSessionStorageKey(token), JSON.stringify({ sessionToken, expiresAt }));
  } catch (error) {
    // Private browsing or a restricted browser may disable sessionStorage.
  }
}

async function postTracking(action, token, extra = {}) {
  return readJson(await fetch(PUBLIC_TRACKING_API, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action, token, ...extra }),
  }));
}

function VerificationCard({ token, onVerified }) {
  const [otpSent, setOtpSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function requestCode() {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const payload = await postTracking("requestEmailOtp", token);
      setOtpSent(true);
      setMessage(payload.message || "驗證碼已寄出，請查看收件匣。");
    } catch (requestError) {
      setError(requestError.message || "驗證碼寄送失敗。");
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(event) {
    event.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setError("請輸入六位數驗證碼。");
      return;
    }
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const payload = await postTracking("verifyEmailOtp", token, { code });
      const tracking = await loadTracking(token, payload.sessionToken);
      if (tracking.authenticated !== true) throw new Error("驗證尚未完成，請重新操作。");
      saveTrackingSession(token, payload.sessionToken, payload.expiresAt);
      onVerified(tracking);
    } catch (verifyError) {
      setError(verifyError.message || "驗證失敗，請重新操作。");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="pt-shell pt-locked-shell">
      <header className="pt-header">
        <img src={TALLARGE_LOGO_URL} alt="Tallarge" className="pt-logo" />
      </header>
      <section className="pt-lock-card">
        <div className="pt-lock-icon"><PublicIcon name="lock" size={25} /></div>
        <h1>驗證後查看配送</h1>
        <p>為保護配送地址與貨物資訊，請先使用系統寄送至收件人的六位數驗證碼。</p>
        {!otpSent ? (
          <button type="button" className="pt-primary-button" onClick={requestCode} disabled={busy}>
            <PublicIcon name="mail" size={18} />{busy ? "正在寄送…" : "寄送 Email 驗證碼"}
          </button>
        ) : (
          <form onSubmit={verifyCode} className="pt-otp-form">
            <label htmlFor="tracking-otp">六位數驗證碼</label>
            <input id="tracking-otp" value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="請輸入 6 位數" maxLength={6} autoFocus />
            <button type="submit" className="pt-primary-button" disabled={busy}>{busy ? "驗證中…" : "驗證並查看配送"}</button>
            <button type="button" className="pt-link-button" onClick={requestCode} disabled={busy}>重新寄送驗證碼</button>
          </form>
        )}
        {message ? <div className="pt-success-message">{message}</div> : null}
        {error ? <div className="pt-error-message">{error}</div> : null}
      </section>
    </main>
  );
}

function deliveryCargoItems(delivery) {
  const directItems = [delivery?.cargoItems, delivery?.items].find(
    (items) => Array.isArray(items) && items.length,
  );
  if (directItems) return directItems;
  const stops = Array.isArray(delivery?.stops) ? delivery.stops : [];
  return stops.flatMap((stop, index) =>
    cargoItemsForStop(stop, delivery, index, stops.length),
  );
}

function deliveryDirection(delivery) {
  const items = deliveryCargoItems(delivery);
  const stops = Array.isArray(delivery?.stops) ? delivery.stops : [];
  const operations = [
    delivery?.operationType,
    delivery?.direction,
    ...stops.map((stop) => stop?.operationType),
    ...items.map((item) => item?.operationType),
  ].map((value) => text(value).toUpperCase());
  const hasPickup = operations.some((value) =>
    ["PICKUP", "PICKUP_DELIVERY", "寄件", "寄出", "取貨"].includes(value),
  );
  const hasDelivery = operations.some((value) =>
    ["DELIVERY", "PICKUP_DELIVERY", "收件", "送貨", "派送", "配送"].includes(value),
  );
  if (hasPickup && hasDelivery) return { label: "寄收", tone: "mixed" };
  if (hasPickup) return { label: "寄件", tone: "pickup" };
  return { label: "收件", tone: "delivery" };
}

function deliveryCargoNameSummary(delivery) {
  const uniqueNames = new Map();
  deliveryCargoItems(delivery).forEach((item) => {
    const name = text(item?.name || item?.itemName || item?.cargoName);
    if (name && !uniqueNames.has(name.toLocaleLowerCase())) {
      uniqueNames.set(name.toLocaleLowerCase(), name);
    }
  });
  const names = Array.from(uniqueNames.values());
  if (!names.length) return "貨物名稱尚未提供";
  if (names.length <= 2) return names.join("、");
  return `${names.slice(0, 2).join("、")} 等 ${names.length} 種`;
}

function DeliveryCard({ delivery, selected, onSelect }) {
  const location = deliveryLocationSummary(delivery);
  const cargoQuantity = delivery.cargoQuantity ?? delivery.cargoCount ?? 0;
  const stationSummary = deliveryStationSummary(delivery);
  const direction = deliveryDirection(delivery);
  const cargoNames = deliveryCargoNameSummary(delivery);
  return (
    <button type="button" className={`pt-delivery-card${selected ? " is-selected" : ""}`} onClick={onSelect}>
      <div className="pt-delivery-card-top">
        <div>
          <span className={`pt-direction-tag ${direction.tone}`}>{direction.label}</span>
          <strong>{delivery.applicationName || "配送工作"}</strong>
        </div>
        <span className={`pt-status-chip ${stationSummary.tone}`}><i />{stationSummary.label}</span>
      </div>
      <div className="pt-delivery-card-meta">
        <span className="pt-location-summary" title={location}><PublicIcon name="pin" size={19} />{location}</span>
        <span className="pt-cargo-summary" title={cargoNames}>
          <PublicIcon name="box" size={19} />
          <span className="pt-cargo-name-summary">{cargoNames}</span>
          <b className="pt-cargo-quantity">{cargoQuantity} 件貨物</b>
        </span>
      </div>
      <div className="pt-delivery-card-action">查看配送進度 <PublicIcon name="arrow" size={18} /></div>
    </button>
  );
}
function DeliveryCalendar({ selectedDateKey, availableDates, onSelectDate }) {
  const initialMonth = calendarMonthFromDate(selectedDateKey);
  const [open, setOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(initialMonth);
  const { year, month } = calendarMonth;
  const days = calendarDays(year, month);

  useEffect(() => {
    if (!selectedDateKey) return;
    setCalendarMonth(calendarMonthFromDate(selectedDateKey));
  }, [selectedDateKey]);

  function changeMonth(offset) {
    const next = new Date(year, month + offset, 1);
    setCalendarMonth({ year: next.getFullYear(), month: next.getMonth() });
  }

  function chooseDate(day) {
    if (!day || !availableDates.has(day.key)) return;
    onSelectDate(day.key);
    setOpen(false);
  }

  return (
    <div className="pt-calendar-wrap">
      <button
        type="button"
        className={`pt-date-picker${open ? " is-open" : ""}`}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <PublicIcon name="calendar" size={22} />
        <span>
          <small>選擇配送日期</small>
          <strong>{formatDate(selectedDateKey)}</strong>
        </span>
        <span className="pt-select-arrow">⌄</span>
      </button>
      {open ? (
        <div className="pt-calendar" role="dialog" aria-label="選擇配送日期">
          <div className="pt-calendar-head">
            <button type="button" onClick={() => changeMonth(-1)} aria-label="上一個月">‹</button>
            <strong>{formatCalendarMonth(year, month)}</strong>
            <button type="button" onClick={() => changeMonth(1)} aria-label="下一個月">›</button>
          </div>
          <div className="pt-calendar-weekdays">
            {[
              "日",
              "一",
              "二",
              "三",
              "四",
              "五",
              "六",
            ].map((weekday) => <span key={weekday}>{weekday}</span>)}
          </div>
          <div className="pt-calendar-grid">
            {days.map((day, index) => {
              const count = day ? availableDates.get(day.key) || 0 : 0;
              const isSelected = day?.key === selectedDateKey;
              return (
                <button
                  type="button"
                  key={day?.key || `empty-${index}`}
                  className={`pt-calendar-day${isSelected ? " is-selected" : ""}${count ? " has-delivery" : ""}`}
                  disabled={!day || !count}
                  onClick={() => chooseDate(day)}
                  aria-label={day ? `${day.key}${count ? `，${count} 筆配送工作` : "，沒有配送工作"}` : undefined}
                >
                  {day ? <><span>{day.day}</span>{count ? <i>{count > 1 ? count : ""}</i> : null}</> : null}
                </button>
              );
            })}
          </div>
          <div className="pt-calendar-note"><i />有配送工作的日期可以選擇</div>
        </div>
      ) : null}
    </div>
  );
}

function deliveryStopAddress(stop) {
  return text(stop?.address || stop?.fullAddress || stop?.locationText || stop?.name || stop?.stopName) ||
    [stop?.city, stop?.suburb, stop?.road, stop?.houseNumber]
      .map(text)
      .filter(Boolean)
      .join("");
}

function deliveryLocationSummary(delivery) {
  const addresses = recipientDeliveryStops(delivery)
    .map(deliveryStopAddress)
    .filter(Boolean);
  if (!addresses.length) return "配送地點尚未提供";
  return addresses.length > 1
    ? `${addresses[0]} ＋另外 ${addresses.length - 1} 個地點`
    : addresses[0];
}

function isCompletedStatus(value) {
  return ["DELIVERED", "COMPLETED", "DONE"].includes(
    text(value).toUpperCase(),
  );
}

function cargoItemsForStop(stop, delivery, stopIndex, totalStops) {
  const parentStopId = text(stop?.id || stop?.stopId);
  const withStopId = (items) => items.map((item) => ({
    ...item,
    stopId: text(item?.stopId || item?.stop?.id) || parentStopId,
  }));
  const directItems = [stop?.cargoItems, stop?.items, stop?.contents].find(
    (items) => Array.isArray(items) && items.length,
  );
  if (directItems) return withStopId(directItems);

  const recipientItems = (Array.isArray(stop?.recipients) ? stop.recipients : [])
    .flatMap((recipient) => {
      if (Array.isArray(recipient?.cargoItems)) return recipient.cargoItems;
      if (Array.isArray(recipient?.contents)) return recipient.contents;
      if (Array.isArray(recipient?.items)) return recipient.items;
      return [];
    });
  if (recipientItems.length) return withStopId(recipientItems);

  const allItems = Array.isArray(delivery?.cargoItems)
    ? delivery.cargoItems
    : Array.isArray(delivery?.items)
      ? delivery.items
      : [];
  if (!allItems.length) return [];
  const stopId = parentStopId.toUpperCase();
  const stopOrder = Number(stop?.order) || stopIndex + 1;
  const matched = allItems.filter((item) => {
    const itemStopId = text(item?.stopId || item?.stop?.id).toUpperCase();
    const itemOrder = Number(item?.stopOrder);
    return (itemStopId && itemStopId === stopId) ||
      (!itemStopId && itemOrder > 0 && itemOrder === stopOrder);
  });
  if (matched.length) return withStopId(matched);
  return totalStops === 1 ? withStopId(allItems) : [];
}

function cargoPhotoKey(item) {
  const itemId = text(item?.id || item?.contentId || item?.cargoId);
  const stopId = text(item?.stopId || item?.stop?.id).toUpperCase();
  return `${stopId}|${itemId}`;
}

function projectedStopFor(delivery, stop) {
  const stopId = text(stop?.id || stop?.stopId).toUpperCase();
  return (Array.isArray(delivery?.executionProjection?.stops)
    ? delivery.executionProjection.stops
    : []).find((item) => text(item?.stopId).toUpperCase() === stopId) || null;
}

function currentStopStatus(delivery, stop) {
  const progress = stop?.progress || {};
  const projected = projectedStopFor(delivery, stop) || {};
  const statusValue = delivery?.isCurrentApplication
    ? progress.status || projected.status || stop?.status
    : projected.status || progress.status || stop?.status;
  const rawStatus = text(statusValue).toLowerCase();
  const completedAt = delivery?.isCurrentApplication
    ? progress.completedAt || projected.completedAt
    : projected.completedAt || progress.completedAt;
  const arrivedAt = delivery?.isCurrentApplication
    ? progress.arrivedAt || projected.arrivedAt
    : projected.arrivedAt || progress.arrivedAt;
  const exceptionSummary = delivery?.isCurrentApplication
    ? stop?.cargoExceptionSummary || projected.cargoExceptionSummary
    : projected.cargoExceptionSummary || stop?.cargoExceptionSummary;
  if (exceptionSummary?.hasException === true || Number(exceptionSummary?.exceptionCount) > 0 || rawStatus === "exception") return "EXCEPTION";
  if (completedAt || ["completed", "done"].includes(rawStatus)) return "COMPLETED";
  if (arrivedAt || rawStatus === "arrived") return "ARRIVED";
  if (rawStatus === "approaching") return "APPROACHING";
  if (["in_progress", "active", "current"].includes(rawStatus)) return "IN_PROGRESS";
  return "PENDING";
}

function stationStatusLabel(value) {
  return {
    PENDING: "待開始",
    APPROACHING: "即將抵達",
    ARRIVED: "已抵達",
    IN_PROGRESS: "進行中",
    COMPLETED: "已完成",
    EXCEPTION: "異常",
  }[text(value).toUpperCase()] || statusLabel(value);
}

function recipientDeliveryStops(delivery, stopsOverride) {
  return Array.isArray(stopsOverride)
    ? stopsOverride
    : Array.isArray(delivery?.stops)
      ? delivery.stops
      : [];
}

function deliveryStationSummary(delivery, stopsOverride) {
  const stops = recipientDeliveryStops(delivery, stopsOverride);
  if (!stops.length) return {label: "站點狀態未提供", status: "PENDING", tone: "blue", completed: 0, total: 0};
  const statuses = stops.map((stop) => currentStopStatus(delivery, stop));
  const completed = statuses.filter((status) => status === "COMPLETED").length;
  if (stops.length === 1) {
    const status = statuses[0];
    return {label: stationStatusLabel(status), status, tone: statusTone(status), completed, total: 1};
  }
  if (statuses.includes("EXCEPTION")) {
    return {label: `${completed}/${stops.length}站 · 有站點異常`, status: "EXCEPTION", tone: "red", completed, total: stops.length};
  }
  if (completed === stops.length) {
    return {label: "全部站點已完成", status: "COMPLETED", tone: "green", completed, total: stops.length};
  }
  const activeIndex = statuses.findIndex((status) => ["IN_PROGRESS", "ARRIVED", "APPROACHING"].includes(status));
  const nextIndex = activeIndex >= 0 ? activeIndex : statuses.findIndex((status) => status !== "COMPLETED");
  const status = statuses[nextIndex] || "PENDING";
  return {
    label: `${completed}/${stops.length}站 · 第${nextIndex + 1}站${stationStatusLabel(status)}`,
    status,
    tone: statusTone(status),
    completed,
    total: stops.length,
  };
}

function projectedCargoFor(delivery, item) {
  const prior = item?.execution && typeof item.execution === "object" ? item.execution : {};
  const itemId = text(item?.id || item?.contentId || item?.cargoId);
  const stopId = text(item?.stopId || item?.stop?.id).toUpperCase();
  const operationType = text(item?.operationType).toUpperCase();
  const candidates = (Array.isArray(delivery?.executionProjection?.cargoItems)
    ? delivery.executionProjection.cargoItems
    : []).filter((candidate) =>
      text(candidate?.id || candidate?.contentId || candidate?.cargoId) === itemId &&
      (!operationType || !text(candidate?.operationType).toUpperCase() || operationType === text(candidate?.operationType).toUpperCase()),
    );
  const exact = candidates.filter((candidate) => {
    const candidateStopId = text(candidate?.stopId || candidate?.stop?.id).toUpperCase();
    return stopId ? candidateStopId === stopId : !candidateStopId;
  });
  const projected = exact.length === 1 ? exact[0] : candidates.length === 1 ? candidates[0] : null;
  const merged = {...prior, ...(projected || {})};
  return Object.keys(merged).length ? merged : null;
}

function cargoProgressLabel(item, execution, deliveryOperationType = "") {
  const status = text(execution?.status || item?.status).toUpperCase();
  if (status === "IN_PROGRESS") {
    const operation = text(item?.operationType || deliveryOperationType).toUpperCase();
    if (operation === "PICKUP") return "前往取貨中";
    if (operation === "DELIVERY") return "配送中";
    return "處理中";
  }
  if (status === "COMPLETED") return "已完成";
  if (status === "EXCEPTION") return "異常";
  return status ? statusLabel(status) : "進度未提供";
}

function cargoExecutionTime(execution) {
  const status = text(execution?.status).toUpperCase();
  if (status === "COMPLETED" && execution?.completedAt) return ["完成時間", execution.completedAt];
  if (status === "EXCEPTION" && execution?.lastExceptionAt) return ["異常時間", execution.lastExceptionAt];
  if (execution?.startedAt) return ["開始時間", execution.startedAt];
  return null;
}

function DeliveryProgressScreen({ delivery, stops, onBack, photoState = {}, evidenceStateByStop = {}, onLoadStationEvidence, onPrepareExport }) {
  const siteStops = recipientDeliveryStops(delivery, stops);
  const totalStops = siteStops.length;
  const completedStops = siteStops.filter((stop) => currentStopStatus(delivery, stop) === "COMPLETED").length;
  const stationSummary = deliveryStationSummary(delivery, siteStops);
  const allItems = siteStops.flatMap((stop, index) => cargoItemsForStop(stop, delivery, index, totalStops));
  const cargoItemCount = allItems.length || Math.max(0, Number(delivery?.cargoCount) || 0);
  const [expandedStops, setExpandedStops] = useState(() =>
    new Set(siteStops.slice(0, 1).map((stop) => stationEvidenceKey(delivery, stop))),
  );
  const [lightbox, setLightbox] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportError, setExportError] = useState("");

  function toggleStop(stopKey) {
    setExpandedStops((current) => {
      const next = new Set(current);
      if (next.has(stopKey)) next.delete(stopKey);
      else next.add(stopKey);
      return next;
    });
  }

  useEffect(() => {
    if (!lightbox) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setLightbox(null);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [lightbox]);

  async function handleExportPdf() {
    if (exportingPdf) return;
    setExportingPdf(true);
    setExportError("");
    setExpandedStops(new Set(siteStops.map((stop) => stationEvidenceKey(delivery, stop))));
    const previousTitle = document.title;
    let cleanupTimer;
    let cleaned = false;
    const finishPrint = () => {
      if (cleaned) return;
      cleaned = true;
      window.removeEventListener("afterprint", finishPrint);
      window.clearTimeout(cleanupTimer);
      document.title = previousTitle;
      setExportingPdf(false);
    };
    try {
      await onPrepareExport?.(delivery, siteStops);
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      const images = Array.from(document.querySelectorAll(
        ".pt-progress-shell .pt-progress-cargo-card img, .pt-progress-shell .pt-signature-card img, .pt-progress-shell .pt-station-media-panel img, .pt-progress-shell .pt-station-route .leaflet-tile",
      ));
      images.forEach((image) => { image.loading = "eager"; });
      await Promise.all(images.map((image) => new Promise((resolve) => {
        if (image.complete) return resolve();
        let timer;
        const finish = () => {
          window.clearTimeout(timer);
          image.removeEventListener("load", finish);
          image.removeEventListener("error", finish);
          resolve();
        };
        image.addEventListener("load", finish, { once: true });
        image.addEventListener("error", finish, { once: true });
        timer = window.setTimeout(finish, 8000);
      })));
      const fileTitle = text(delivery.applicationName || "Tallarge 配送詳情")
        .replace(/[\\/:*?"<>|]/g, "-")
        .slice(0, 80);
      document.title = `${fileTitle} - 完整配送詳情`;
      window.addEventListener("afterprint", finishPrint, { once: true });
      cleanupTimer = window.setTimeout(finishPrint, 60000);
      window.print();
    } catch (error) {
      finishPrint();
      setExportError(text(error?.message) || "PDF 匯出準備失敗，請稍後重試。");
    }
  }

  return (
    <main className="pt-shell pt-progress-shell">
      <header className="pt-progress-header">
        <div className="pt-progress-brand">
          <button type="button" className="pt-back-button" onClick={onBack} aria-label="返回配送清單">‹</button>
          <img src={TALLARGE_LOGO_URL} alt="Tallarge" className="pt-logo" />
        </div>
        <div className="pt-progress-header-actions">
          <span className={`pt-status-chip ${stationSummary.tone}`}><i />{stationSummary.label}</span>
          <button type="button" className="pt-pdf-button" onClick={handleExportPdf} disabled={exportingPdf}>
            {exportingPdf ? "正在整理…" : "匯出完整 PDF"}
          </button>
        </div>
      </header>
      {exportError ? <p className="pt-export-error" role="alert">{exportError}</p> : null}
      <section className="pt-progress-title">
        <span className="pt-eyebrow">配送進度</span>
        <h1>{delivery.applicationName || "配送工作"}</h1>
        <p>{formatDate(delivery.startAt || delivery.dateKey)}<span aria-hidden="true"> · </span>{totalStops} 個站點</p>
      </section>
      <section className="pt-progress-summary" aria-label="配送工作摘要">
        <div className="pt-progress-stat">
          <small>站點數量</small>
          <strong>{totalStops} 個站點</strong>
        </div>
        <div className="pt-progress-stat">
          <small>貨物項目</small>
          <strong>{cargoItemCount} 個貨物</strong>
        </div>
      </section>
      <section className="pt-route-section">
        <div className="pt-route-heading">
          <h2>配送紀錄</h2>
          <span>{totalStops} 個站點</span>
        </div>
        {photoState.status === "loading" ? <p className="pt-photo-status">正在載入貨物照片…</p> : null}
        {photoState.status === "error" ? <p className="pt-photo-status">照片暫時無法載入，配送進度仍可正常查看。</p> : null}
        {totalStops ? (
          <div className="pt-route-list">
            {siteStops.map((stop, index) => {
              const stopProjection = projectedStopFor(delivery, stop) || {};
              const stopStatus = currentStopStatus(delivery, stop);
              const items = cargoItemsForStop(stop, delivery, index, totalStops);
              const stopKey = stationEvidenceKey(delivery, stop);
              const evidenceState = evidenceStateByStop[stopKey] || {};
              const expanded = expandedStops.has(stopKey);
              const completed = stopStatus === "COMPLETED";
              const active = !completed && statusTone(stopStatus) === "orange";
              const progress = delivery?.isCurrentApplication
                ? { ...stopProjection, ...(stop?.progress || {}) }
                : { ...(stop?.progress || {}), ...stopProjection };
              const headerTimes = [
                progress.startedAt ? `開始 ${formatClockTime(progress.startedAt)}` : "",
                progress.completedAt ? `完成 ${formatClockTime(progress.completedAt)}` : "",
              ].filter(Boolean);
              const signature = evidenceState.signature?.url ? evidenceState.signature : null;
              const stationMedia = [];
              if (evidenceState.status === "ready") {
                const seenUrls = new Set();
                items.forEach((item) => {
                  const itemName = text(item?.name || item?.itemName || item?.cargoName) || "貨物";
                  const media = evidenceState.mediaByCargoKey?.[cargoPhotoKey(item)] || {};
                  const assets = [
                    ...(Array.isArray(media.photos) ? media.photos.map((asset) => ({ ...asset, kind: "photo" })) : []),
                    ...(Array.isArray(media.videos) ? media.videos.map((asset) => ({ ...asset, kind: "video" })) : []),
                  ];
                  assets.forEach((asset) => {
                    const url = text(asset.url);
                    if (!url || seenUrls.has(url)) return;
                    seenUrls.add(url);
                    stationMedia.push({ ...asset, itemName });
                  });
                });
              }
              const timelineEvents = [
                { key: "approaching", label: "接近站點", time: progress.approachingAt },
                { key: "arrived", label: "抵達站點", time: progress.arrivedAt },
                { key: "started", label: "開始作業", time: progress.startedAt },
                { key: "signature", label: "簽收紀錄", time: signature?.signedAt, signature: true },
                { key: "completed", label: "貨物完成", time: progress.completedAt },
              ].filter((event) => event.time || event.signature);
              return (
                <article className={`pt-route-item${completed ? " is-complete" : active ? " is-active" : ""}`} key={stopKey || `stop-${index}`}>
                  <div className="pt-route-rail">
                    <span className="pt-route-marker">{completed ? <PublicIcon name="check" size={18} /> : String(index + 1).padStart(2, "0")}</span>
                    {index < totalStops - 1 ? <i /> : null}
                  </div>
                  <div className="pt-route-card">
                    <button
                      type="button"
                      className="pt-station-toggle"
                      aria-expanded={expanded}
                      aria-controls={`pt-station-body-${index}`}
                      onClick={() => toggleStop(stopKey)}
                    >
                      <span className="pt-station-toggle-main">
                        <span className="pt-station-sequence">站點 {String(index + 1).padStart(2, "0")}</span>
                        <strong>{deliveryStopAddress(stop) || "配送地址尚未提供"}</strong>
                        {headerTimes.length ? <small>{headerTimes.join("　·　")}</small> : null}
                      </span>
                      <span className="pt-station-toggle-side">
                        <span className={`pt-route-status ${statusTone(stopStatus)}`}><i />{stationStatusLabel(stopStatus)}</span>
                        <span className={`pt-station-chevron${expanded ? " is-open" : ""}`} aria-hidden="true">⌄</span>
                      </span>
                    </button>
                    {expanded ? (
                      <div className="pt-station-body" id={`pt-station-body-${index}`}>
                        <section className="pt-cargo-section" aria-label="本站貨物資訊">
                          <div className="pt-station-subheading"><PublicIcon name="box" size={18} /><h4>貨物資訊</h4><span>{items.length} 項</span></div>
                          <div className="pt-progress-cargo-list">
                            {items.length ? items.map((item) => {
                              const execution = projectedCargoFor(delivery, item);
                              const itemStatus = text(execution?.status || item?.status);
                              const quantity = item?.quantity ?? item?.totalQuantity ?? item?.qty ?? 0;
                              const unit = text(item?.unit) || "件";
                              const executionTime = cargoExecutionTime(execution);
                              const photos = photoState.photosByCargoKey?.[cargoPhotoKey(item)] || [];
                              const itemName = text(item?.name || item?.itemName || item?.cargoName) || "未命名貨物";
                              return (
                                <article className="pt-progress-cargo-card" key={`${text(item?.stopId || stop?.id || stop?.stopId)}|${text(item?.id || item?.contentId)}|${text(item?.operationType)}`}>
                                  <div className="pt-cargo-card-head">
                                    <div><small>貨物名稱</small><strong className="pt-progress-cargo-name">{itemName}</strong></div>
                                    <strong className="pt-cargo-quantity-large">{quantity} <small>{unit}</small></strong>
                                  </div>
                                  <div className="pt-cargo-card-meta">
                                    <em className={statusTone(itemStatus)}>{cargoProgressLabel(item, execution, delivery.operationType)}</em>
                                    {executionTime ? <small>{executionTime[0]}：{formatDateTime(executionTime[1])}</small> : null}
                                  </div>
                                  {photos.length ? (
                                    <div className="pt-cargo-photo-block" aria-label={`${itemName}貨物照片`}>
                                      <span>貨物照片</span>
                                      <div className="pt-cargo-photo-list">
                                        {photos.map((photo, photoIndex) => (
                                          <button key={`${photo.previewUrl}-${photoIndex}`} type="button" className="pt-image-thumb" onClick={() => setLightbox({ src: photo.originalUrl || photo.previewUrl, alt: `${itemName}貨物照片 ${photoIndex + 1}`, caption: `${itemName} · 貨物照片` })} aria-label={`在頁面內放大${itemName}貨物照片 ${photoIndex + 1}`}>
                                            <img className="pt-cargo-photo" src={photo.previewUrl} alt={`${itemName}貨物照片 ${photoIndex + 1}`} loading="lazy" referrerPolicy="no-referrer" />
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  ) : null}
                                </article>
                              );
                            }) : <p className="pt-no-cargo">目前沒有可顯示的貨物明細</p>}
                          </div>
                        </section>
                        <div className="pt-station-evidence">
                          <button
                            type="button"
                            className="pt-evidence-button"
                            onClick={() => onLoadStationEvidence?.(delivery, stop, evidenceState.status === "ready")}
                            disabled={evidenceState.status === "loading"}
                          >
                            {evidenceState.status === "loading" ? "正在載入本站照片、簽收與路線…" : evidenceState.status === "ready" ? "本站資料已查詢 · 點擊重新整理" : "查看本站照片、影片、簽收與行車路線"}
                          </button>
                          {evidenceState.status === "error" ? <p className="pt-photo-status">目前無法載入本站附件與路線，配送進度仍可正常查看。{evidenceState.message ? ` ${evidenceState.message}` : ""}</p> : null}
                          {evidenceState.status === "ready" ? (
                            <div className="pt-evidence-detail">
                              <section className="pt-station-worklog" aria-label="本站簽收紀錄">
                                <div className="pt-station-subheading"><PublicIcon name="check" size={18} /><h4>簽收與完成紀錄</h4></div>
                                {timelineEvents.length ? (
                                  <ol className="pt-station-timeline">
                                    {timelineEvents.map((event) => (
                                      <li className={`pt-timeline-event${event.signature ? " is-signature" : ""}`} key={event.key}>
                                        <time>{formatClockTime(event.time) || "—"}</time>
                                        <span className="pt-timeline-dot" />
                                        <div className="pt-timeline-content">
                                          <strong>{event.label}</strong>
                                          {event.signature ? signature ? (
                                            <figure className="pt-signature-card">
                                              <button type="button" className="pt-image-thumb" onClick={() => setLightbox({ src: signature.originalUrl || signature.url, alt: "收件人簽收原圖", caption: "收件人簽收" })} aria-label="在頁面內放大收件人簽收原圖">
                                                <img src={signature.url} alt="收件人簽收" loading="lazy" referrerPolicy="no-referrer" />
                                              </button>
                                              <figcaption>收件人簽收{signature.signedAt ? ` · ${formatDateTime(signature.signedAt)}` : ""}</figcaption>
                                            </figure>
                                          ) : <span className="pt-timeline-note">本站尚無簽收影像。</span> : null}
                                          {event.key === "completed" ? <span className="pt-timeline-note">已完成 {items.length} 項貨物</span> : null}
                                        </div>
                                      </li>
                                    ))}
                                  </ol>
                                ) : <p className="pt-photo-status">本站尚無簽收或完成時間紀錄。</p>}
                              </section>
                              <section className="pt-station-media-panel" aria-label="本站現場照片與文件">
                                <div className="pt-station-subheading"><PublicIcon name="box" size={18} /><h4>現場照片 / 文件</h4><span>{stationMedia.length} 項</span></div>
                                {stationMedia.length ? (
                                  <div className="pt-station-media-gallery">
                                    {stationMedia.map((asset, mediaIndex) => asset.kind === "video" ? (
                                      <figure className="pt-evidence-video" key={`${asset.url}-${mediaIndex}`}>
                                        <video controls preload="metadata" src={asset.url} />
                                        <figcaption>{asset.itemName} · {asset.stage === "pickup" ? "取貨影片" : "現場影片"}{asset.occurredAt ? ` · ${formatDateTime(asset.occurredAt)}` : ""}</figcaption>
                                      </figure>
                                    ) : (
                                      <figure className="pt-evidence-media" key={`${asset.url}-${mediaIndex}`}>
                                        <button type="button" className="pt-image-thumb" onClick={() => setLightbox({ src: asset.originalUrl || asset.url, alt: `${asset.itemName}現場照片 ${mediaIndex + 1}`, caption: `${asset.itemName} · ${asset.stage === "pickup" ? "取貨照片" : "現場照片"}` })} aria-label={`在頁面內放大${asset.itemName}現場照片`}>
                                          <img src={asset.url} alt={`${asset.itemName}現場照片 ${mediaIndex + 1}`} loading="lazy" referrerPolicy="no-referrer" />
                                        </button>
                                        <figcaption>{asset.itemName} · {asset.stage === "pickup" ? "取貨照片" : "現場照片"}{asset.occurredAt ? ` · ${formatDateTime(asset.occurredAt)}` : ""}</figcaption>
                                      </figure>
                                    ))}
                                  </div>
                                ) : <p className="pt-photo-status">本站尚無現場照片或影片。</p>}
                              </section>
                              <details className="pt-station-route" open>
                                <summary className="pt-station-route-head"><strong>查看定位軌跡</strong></summary>
                                {evidenceState.route?.available ? (
                                  <>
                                    <PublicRouteMap points={evidenceState.route.points} />
                                  </>
                                ) : <p className="pt-photo-status">{evidenceState.route?.reason || "目前沒有可顯示的 GPS 路線。"}</p>}
                              </details>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        ) : <div className="pt-empty">目前沒有可顯示的配送地點。</div>}
      </section>
      <footer className="pt-footer"><strong className="pt-completion-footer">{completedStops} 個站點已完成</strong><br />各站點狀態會隨成員回報更新<br />配送完成後 3 天，查看權限將自動失效</footer>
      {lightbox ? (
        <div className="pt-image-lightbox" role="dialog" aria-modal="true" aria-label={lightbox.alt} onClick={() => setLightbox(null)}>
          <button type="button" className="pt-image-lightbox-close" onClick={() => setLightbox(null)} aria-label="關閉原圖">×</button>
          <div className="pt-image-lightbox-content" onClick={(event) => event.stopPropagation()}>
            <img src={lightbox.src} alt={lightbox.alt} referrerPolicy="no-referrer" />
            <p>{lightbox.caption} · 點擊背景或按 Esc 關閉</p>
          </div>
        </div>
      ) : null}
    </main>
  );
}
function TrackingContent({ data, token }) {
  const application = data.application || {};
  const deliveries = Array.isArray(data.deliveries) ? data.deliveries : [];
  const detailHistoryPushed = useRef(false);
  const currentDelivery = {
    applicationId: data.applicationId || "current",
    isCurrentApplication: true,
    dateKey: data.dateKey || dateKey(application.schedule?.startAt),
    startAt: application.schedule?.startAt || data.startAt || null,
    applicationName: application.applicationName || "配送工作",
    stops: Array.isArray(application.stops) ? application.stops : [],
    cargoItems: Array.isArray(application.cargoItems) ? application.cargoItems : [],
    stopCount: application.stopCount ?? (Array.isArray(application.stops) ? application.stops.length : 0),
    cargoQuantity: application.cargoQuantity ?? (Array.isArray(application.cargoItems)
      ? application.cargoItems.reduce((total, item) => total + Number(item.quantity || 0), 0)
      : 0),
    cargoCount: application.cargoCount ?? (Array.isArray(application.cargoItems) ? application.cargoItems.length : 0),
  };
  const rows = useMemo(() => {
    const merged = new Map();
    [currentDelivery, ...deliveries].forEach((delivery) => {
      const key = `${deliveryDateKey(delivery)}|${delivery.applicationId || delivery.applicationName}`;
      if (!merged.has(key)) merged.set(key, delivery);
    });
    return [...merged.values()].sort((a, b) => deliveryDateKey(b).localeCompare(deliveryDateKey(a)));
  }, [data, deliveries]);
  const availableDates = useMemo(() => {
    const counts = new Map();
    rows.forEach((delivery) => {
      const key = deliveryDateKey(delivery);
      if (key) counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [rows]);
  const [selectedDateKey, setSelectedDateKey] = useState("");
  const [selectedKey, setSelectedKey] = useState("");
  const [activeDeliveryKey, setActiveDeliveryKey] = useState("");
  const [photoStateByDelivery, setPhotoStateByDelivery] = useState({});
  const photoRequests = useRef(new Map());
  const [evidenceStateByStop, setEvidenceStateByStop] = useState({});
  const evidenceRequests = useRef(new Map());
  const activeDateKey = selectedDateKey || deliveryDateKey(rows[0]);
  const selectedRows = rows.filter((delivery) => deliveryDateKey(delivery) === activeDateKey);
  const selected = selectedRows.find((delivery) => `${deliveryDateKey(delivery)}|${delivery.applicationId || delivery.applicationName}` === selectedKey) || selectedRows[0] || rows[0] || currentDelivery;
  const selectedIsCurrent = selected.applicationId === currentDelivery.applicationId && deliveryDateKey(selected) === deliveryDateKey(currentDelivery);
  const selectedStops = recipientDeliveryStops(
    selectedIsCurrent ? currentDelivery : selected,
  );

  useEffect(() => {
    if (!rows.length) return;
    const nextDateKey = selectedDateKey || deliveryDateKey(rows[0]);
    const dateRows = rows.filter((delivery) => deliveryDateKey(delivery) === nextDateKey);
    const currentSelectedStillExists = dateRows.some((delivery) => `${deliveryDateKey(delivery)}|${delivery.applicationId || delivery.applicationName}` === selectedKey);
    if (!selectedDateKey) setSelectedDateKey(nextDateKey);
    if (!currentSelectedStillExists && dateRows[0]) {
      setSelectedKey(`${deliveryDateKey(dateRows[0])}|${dateRows[0].applicationId || dateRows[0].applicationName}`);
    }
  }, [rows, selectedDateKey, selectedKey]);

  useEffect(() => {
    const handlePopState = () => {
      detailHistoryPushed.current = false;
      setActiveDeliveryKey("");
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (activeDeliveryKey) window.scrollTo({ top: 0, behavior: "auto" });
  }, [activeDeliveryKey]);

  async function ensureCargoPhotoPreviews(delivery) {
    const applicationId = text(delivery?.applicationId);
    if (!applicationId || applicationId === "current") return;
    const cacheKey = `${deliveryDateKey(delivery)}|${applicationId}`;
    if (photoStateByDelivery[cacheKey]?.status === "ready") return;
    if (photoRequests.current.has(cacheKey)) return photoRequests.current.get(cacheKey);
    if (photoStateByDelivery[cacheKey]?.status === "loading") return;
    const savedSession = readSavedTrackingSession(token);
    if (!savedSession?.sessionToken) {
      setPhotoStateByDelivery((current) => ({ ...current, [cacheKey]: { status: "error" } }));
      return;
    }
    setPhotoStateByDelivery((current) => ({ ...current, [cacheKey]: { status: "loading" } }));
    const request = (async () => {
      try {
        const payload = await postTracking("getCargoPhotoPreviews", token, {
          session: savedSession.sessionToken,
          applicationId,
        });
        setPhotoStateByDelivery((current) => ({
          ...current,
          [cacheKey]: {
            status: "ready",
            photosByCargoKey: payload.photosByCargoKey || {},
          },
        }));
      } catch (error) {
        console.warn("[PublicTrackingPage] cargo photo preview unavailable", {
          applicationId,
          message: text(error?.message),
        });
        setPhotoStateByDelivery((current) => ({ ...current, [cacheKey]: { status: "error" } }));
      } finally {
        photoRequests.current.delete(cacheKey);
      }
    })();
    photoRequests.current.set(cacheKey, request);
    return request;
  }

  async function ensureStationEvidence(delivery, stop, forceRefresh = false) {
    const applicationId = text(delivery?.applicationId);
    const stopId = text(stop?.id || stop?.stopId);
    if (!applicationId || applicationId === "current" || !stopId) return;
    const cacheKey = stationEvidenceKey(delivery, stop);
    const evidenceStatus = evidenceStateByStop[cacheKey]?.status;
    if (!forceRefresh && evidenceStatus === "ready") return;
    if (evidenceRequests.current.has(cacheKey)) return evidenceRequests.current.get(cacheKey);
    if (evidenceStatus === "loading") return;
    const savedSession = readSavedTrackingSession(token);
    if (!savedSession?.sessionToken) {
      setEvidenceStateByStop((current) => ({...current, [cacheKey]: {status: "error"}}));
      return;
    }
    setEvidenceStateByStop((current) => ({...current, [cacheKey]: {status: "loading"}}));
    const request = (async () => {
      try {
        const payload = await postTracking("getRecipientStopEvidence", token, {
          session: savedSession.sessionToken,
          applicationId,
          stopId,
        });
        const evidenceGroups = Object.values(payload.mediaByCargoKey || {});
        const photoCount = evidenceGroups.reduce(
          (count, group) => count + (Array.isArray(group?.photos) ? group.photos.length : 0),
          0,
        );
        const videoCount = evidenceGroups.reduce(
          (count, group) => count + (Array.isArray(group?.videos) ? group.videos.length : 0),
          0,
        );
        console.info("[PublicTrackingPage] station evidence response", {
          applicationId,
          stopId,
          apiOk: true,
          returned: {
            photos: photoCount,
            videos: videoCount,
            signature: Boolean(payload.signature?.url),
            routeAvailable: payload.route?.available === true,
            routePointCount: Number(payload.route?.pointCount) || 0,
          },
          backendDiagnosticsAvailable: Boolean(payload.diagnostics),
          backendDiagnostics: payload.diagnostics || null,
        });
        setEvidenceStateByStop((current) => ({
          ...current,
          [cacheKey]: {
            status: "ready",
            mediaByCargoKey: payload.mediaByCargoKey || {},
            signature: payload.signature || null,
            route: payload.route || {available: false, reason: "目前沒有可顯示的 GPS 路線。"},
          },
        }));
      } catch (error) {
        console.warn("[PublicTrackingPage] station evidence unavailable", {
          applicationId,
          stopId,
          message: text(error?.message),
        });
        setEvidenceStateByStop((current) => ({
          ...current,
          [cacheKey]: {status: "error", message: text(error?.message)},
        }));
      } finally {
        evidenceRequests.current.delete(cacheKey);
      }
    })();
    evidenceRequests.current.set(cacheKey, request);
    return request;
  }

  async function prepareDeliveryExport(delivery, exportStops) {
    await ensureCargoPhotoPreviews(delivery);
    await Promise.all(exportStops.map((stop) => ensureStationEvidence(delivery, stop)));
  }

  function handleDateSelect(nextDateKey) {
    const firstDelivery = rows.find((delivery) => deliveryDateKey(delivery) === nextDateKey);
    setSelectedDateKey(nextDateKey);
    if (firstDelivery) {
      setSelectedKey(`${deliveryDateKey(firstDelivery)}|${firstDelivery.applicationId || firstDelivery.applicationName}`);
    }
  }

  function handleSelectDelivery(delivery) {
    const nextKey = `${deliveryDateKey(delivery)}|${delivery.applicationId || delivery.applicationName}`;
    setSelectedDateKey(deliveryDateKey(delivery));
    setSelectedKey(nextKey);
    if (!detailHistoryPushed.current) {
      const previousState = window.history.state && typeof window.history.state === "object"
        ? window.history.state
        : {};
      window.history.pushState({ ...previousState, tallargeTrackingDetail: true }, "", window.location.href);
      detailHistoryPushed.current = true;
    }
    setActiveDeliveryKey(nextKey);
    void ensureCargoPhotoPreviews(delivery);
  }

  function handleBackToList() {
    if (detailHistoryPushed.current) {
      window.history.back();
      return;
    }
    setActiveDeliveryKey("");
  }

  if (activeDeliveryKey) {
    const photoState = photoStateByDelivery[activeDeliveryKey] || {};
    return <DeliveryProgressScreen key={activeDeliveryKey} delivery={selected} stops={selectedStops} onBack={handleBackToList} photoState={photoState} evidenceStateByStop={evidenceStateByStop} onLoadStationEvidence={ensureStationEvidence} onPrepareExport={prepareDeliveryExport} />;
  }

  return (
    <main className="pt-shell">
      <header className="pt-header"><img src={TALLARGE_LOGO_URL} alt="Tallarge" className="pt-logo" /><span className="pt-safe-badge"><PublicIcon name="check" size={14} />已驗證</span></header>
      <section className="pt-page-title"><h1>我的配送</h1></section>
      <DeliveryCalendar selectedDateKey={activeDateKey} availableDates={availableDates} onSelectDate={handleDateSelect} />
      <p className="pt-date-summary">{selectedRows.length ? `目前共有 ${selectedRows.length} 筆配送工作` : "這一天沒有配送工作"}</p>
      <section className="pt-list-section"><div className="pt-section-heading"><h2>配送工作</h2></div>{selectedRows.length ? selectedRows.map((delivery) => { const key = `${deliveryDateKey(delivery)}|${delivery.applicationId || delivery.applicationName}`; return <DeliveryCard key={key} delivery={delivery} selected={key === selectedKey} onSelect={() => handleSelectDelivery(delivery)} />; }) : <div className="pt-empty">請從行事曆選擇有配送工作的日期。</div>}</section>
      <footer className="pt-footer">此頁面只顯示已驗證收件人的配送資訊<br />配送完成後 3 天，查看權限將自動失效</footer>
    </main>
  );
}

function PublicTrackingPage() {
  const [token, setToken] = useState("");
  const [state, setState] = useState({ loading: true, locked: false, data: null, error: "" });

  useEffect(() => {
    let active = true;
    const nextToken = readTokenFromLocation();
    setToken(nextToken);
    if (!nextToken) {
      setState({ loading: false, locked: false, data: null, error: "這個追蹤連結缺少 Token。" });
      return () => { active = false; };
    }
    const savedSession = readSavedTrackingSession(nextToken);
    loadTracking(nextToken, savedSession?.sessionToken || "").then((payload) => {
      if (!active) return;
      if (payload.authenticated === false || payload.requiresEmailVerification) {
        try { sessionStorage.removeItem(trackingSessionStorageKey(nextToken)); } catch (error) { /* ignore */ }
        setState({ loading: false, locked: true, data: null, error: "" });
      }
      else setState({ loading: false, locked: false, data: payload, error: "" });
    }).catch((error) => {
      if (active) setState({ loading: false, locked: false, data: null, error: error.message || "這個追蹤連結目前無法使用。" });
    });
    return () => { active = false; };
  }, []);

  if (state.loading) return <main className="pt-shell pt-loading"><div className="pt-spinner" /><h1>正在確認配送連結</h1></main>;
  if (state.error || !state.data && !state.locked) return <main className="pt-shell pt-loading"><div className="pt-error-icon">!</div><h1>追蹤連結無法使用</h1><p>{state.error || "這個連結不存在或已失效。"}</p></main>;
  if (state.locked) return <VerificationCard token={token} onVerified={(data) => setState({ loading: false, locked: false, data, error: "" })} />;
  return <TrackingContent data={state.data} token={token} />;
}

const publicTrackingStyles = `
*{box-sizing:border-box}.pt-shell{width:min(100%,760px);min-height:100vh;margin:0 auto;padding:0 24px 60px;color:#123d63;font-family:Inter,"Noto Sans TC","Microsoft JhengHei",sans-serif}.pt-header{height:92px;display:flex;align-items:center;justify-content:space-between}.pt-logo{display:block;width:178px;max-width:68%;height:auto}.pt-safe-badge{display:inline-flex;align-items:center;gap:5px;padding:8px 11px;border-radius:999px;background:#edf9f2;color:#198754;font-size:11px;font-weight:850}.pt-eyebrow{color:#9aaec0;font-size:10px;font-weight:950;letter-spacing:.18em}.pt-page-title h1{margin:7px 0 22px;font-size:36px;line-height:1.2;letter-spacing:-.04em}.pt-date-picker{display:flex;align-items:center;gap:14px;padding:16px 18px;border:1px solid #e4edf3;border-radius:17px;background:#f1f8fe;cursor:pointer}.pt-date-picker>span:nth-child(2){display:grid;gap:3px;flex:1;min-width:0}.pt-date-picker small{color:#6f879b;font-size:10px;font-weight:800}.pt-date-picker select{width:100%;border:0;outline:0;background:transparent;color:#123d63;font:800 17px Inter,"Noto Sans TC",sans-serif;appearance:none;cursor:pointer}.pt-select-arrow{font-size:24px;color:#123d63;line-height:1;transform:translateY(-3px)}.pt-date-summary{margin:17px 2px 30px;color:#778c9e;font-size:14px}.pt-list-section{margin-top:28px}.pt-section-heading{margin:0 2px 11px}.pt-section-heading>span{color:#9aaec0;font-size:10px;font-weight:950;letter-spacing:.18em}.pt-section-heading h2{margin:5px 0 0;font-size:22px;letter-spacing:-.02em}.pt-delivery-card{display:block;width:100%;margin-top:12px;padding:20px 19px;border:1px solid #e1ebf2;border-radius:18px;background:#fff;color:#123d63;text-align:left;box-shadow:0 8px 23px rgba(23,65,98,.045);cursor:pointer;transition:.18s}.pt-delivery-card:hover,.pt-delivery-card.is-selected{border-color:#f58220;box-shadow:0 10px 27px rgba(245,130,32,.13)}.pt-delivery-card-top{display:flex;align-items:flex-start;justify-content:space-between;gap:13px}.pt-delivery-card-top>.pt-status-chip{max-width:48%;white-space:normal;line-height:1.3}.pt-delivery-card strong{display:block;font-size:20px;line-height:1.25}.pt-status-chip{display:inline-flex;align-items:center;gap:6px;flex:none;padding:8px 11px;border-radius:999px;font-size:11px;font-weight:900}.pt-status-chip i{display:inline-block;width:7px;height:7px;border-radius:50%;background:currentColor}.pt-status-chip.green{background:#eaf8f0;color:#198754}.pt-status-chip.orange{background:#fff4e8;color:#d97706}.pt-status-chip.blue{background:#edf5ff;color:#276aa5}.pt-status-chip.red{background:#fff0f0;color:#c74646}.pt-delivery-card-meta{display:flex;gap:20px;margin-top:18px;padding-bottom:16px;border-bottom:1px solid #e9f0f4;color:#2b5271;font-size:13px;font-weight:750}.pt-delivery-card-meta span{display:inline-flex;align-items:center;gap:6px}.pt-delivery-card-action{display:flex;align-items:center;justify-content:center;gap:6px;margin-top:14px;padding:12px;border-radius:11px;background:#f3f8fc;color:#164a73;font-size:14px;font-weight:850}.pt-empty{padding:24px;border:1px dashed #dce8ef;border-radius:15px;color:#8ba0af;text-align:center;font-size:13px}.pt-footer{padding:30px 0 0;color:#9aabb8;text-align:center;font-size:11px;line-height:1.8}.pt-locked-shell{display:flex;flex-direction:column}.pt-lock-card{margin-top:60px;padding:34px 27px;border:1px solid #e2edf3;border-radius:24px;background:#fff;box-shadow:0 14px 40px rgba(23,65,98,.07);text-align:center}.pt-lock-icon{display:grid;place-items:center;width:58px;height:58px;margin:0 auto 18px;border-radius:18px;background:#eef7ff;color:#1b527d}.pt-lock-card h1{margin:8px 0 10px;font-size:27px}.pt-lock-card p{margin:0 auto 24px;max-width:430px;color:#718899;font-size:13px;line-height:1.8}.pt-primary-button{display:flex;align-items:center;justify-content:center;gap:8px;width:100%;min-height:48px;border:0;border-radius:12px;background:#f58220;color:#fff;font-size:14px;font-weight:900;cursor:pointer}.pt-primary-button:disabled{opacity:.6;cursor:wait}.pt-otp-form{display:grid;gap:10px;text-align:left}.pt-otp-form label{color:#4f6a7d;font-size:12px;font-weight:850}.pt-otp-form input{width:100%;height:53px;border:1px solid #d9e6ee;border-radius:12px;outline:0;color:#123d63;text-align:center;letter-spacing:.35em;font-size:24px;font-weight:900}.pt-otp-form input:focus{border-color:#f58220;box-shadow:0 0 0 3px rgba(245,130,32,.12)}.pt-link-button{border:0;background:transparent;color:#39749c;font-size:12px;font-weight:850;cursor:pointer}.pt-link-button:disabled{opacity:.5}.pt-success-message,.pt-error-message{margin-top:15px;padding:11px 12px;border-radius:10px;font-size:12px;line-height:1.6}.pt-success-message{background:#edf9f2;color:#198754}.pt-error-message{background:#fff1f1;color:#bd4646}.pt-security-note{display:block;margin-top:21px;color:#9aabb8;font-size:10px;line-height:1.7}.pt-loading{display:grid;place-items:center;align-content:center;min-height:100vh;text-align:center}.pt-loading h1{margin:16px 0 0;font-size:21px}.pt-loading p{color:#718899;font-size:13px}.pt-spinner{width:30px;height:30px;border:3px solid #e4edf3;border-top-color:#f58220;border-radius:50%;animation:pt-spin .8s linear infinite}.pt-error-icon{display:grid;place-items:center;width:50px;height:50px;border-radius:16px;background:#fff0f0;color:#c74646;font-size:24px;font-weight:950}@keyframes pt-spin{to{transform:rotate(360deg)}}
.pt-photo-status{margin:8px 0 0;color:#8295a5;font-size:11px;line-height:1.6}
.pt-stop-execution-times{display:flex;flex-wrap:wrap;gap:6px 10px;margin-top:9px;color:#8295a5;font-size:10px;line-height:1.5}
.pt-cargo-execution-time{display:block;grid-column:1/-1;color:#8295a5;font-size:10px;line-height:1.5}
.pt-cargo-photo-list{display:flex;gap:8px;grid-column:1/-1;margin-top:4px;overflow-x:auto;padding-bottom:2px}
.pt-cargo-photo{display:block;width:72px;height:72px;flex:none;object-fit:cover;border:1px solid #e2ebf1;border-radius:11px;background:#f3f7fa}
@media(max-width:560px){.pt-cargo-photo{width:64px;height:64px}.pt-stop-execution-times{font-size:9px}}
@media(max-width:560px){.pt-shell{padding:0 16px 44px}.pt-header{height:78px}.pt-logo{width:164px}.pt-page-title h1{font-size:32px;margin-bottom:19px}.pt-date-picker{padding:14px}.pt-date-picker select{font-size:16px}.pt-delivery-card{padding:17px 15px}.pt-delivery-card strong{font-size:18px}.pt-status-chip{padding:7px 9px;font-size:10px}.pt-delivery-card-meta{gap:12px;font-size:12px}.pt-delivery-card-action{font-size:13px}.pt-lock-card{margin-top:38px;padding:29px 20px}.pt-lock-card h1{font-size:24px}.pt-safe-badge{font-size:10px}}
.pt-calendar-wrap{position:relative;width:100%;z-index:4}.pt-date-picker{width:100%;border:1px solid #e4edf3;color:#123d63;text-align:left;font-family:inherit}.pt-date-picker>span:nth-child(2){display:grid;gap:3px;flex:1;min-width:0}.pt-date-picker strong{display:block;color:#123d63;font-size:17px;line-height:1.25}.pt-date-picker.is-open{border-color:#f58220;box-shadow:0 0 0 3px rgba(245,130,32,.1)}.pt-calendar{position:absolute;top:calc(100% + 9px);left:0;right:0;padding:16px;border:1px solid #e2edf3;border-radius:18px;background:#fff;box-shadow:0 18px 40px rgba(23,65,98,.14)}.pt-calendar-head{display:grid;grid-template-columns:38px 1fr 38px;align-items:center;gap:8px;margin-bottom:12px}.pt-calendar-head strong{text-align:center;font-size:15px}.pt-calendar-head button{display:grid;place-items:center;width:38px;height:34px;border:0;border-radius:10px;background:#f3f8fc;color:#164a73;font-size:25px;line-height:1;cursor:pointer}.pt-calendar-head button:hover{background:#e8f3fa}.pt-calendar-weekdays,.pt-calendar-grid{display:grid;grid-template-columns:repeat(7,1fr);gap:5px}.pt-calendar-weekdays{margin-bottom:5px;color:#91a4b3;text-align:center;font-size:11px;font-weight:850}.pt-calendar-day{position:relative;display:grid;place-items:center;min-height:38px;border:0;border-radius:11px;background:#fff;color:#c4ced6;font:700 13px Inter,"Noto Sans TC",sans-serif;cursor:not-allowed}.pt-calendar-day.has-delivery{color:#164a73;cursor:pointer}.pt-calendar-day.has-delivery:hover{background:#edf6fc}.pt-calendar-day.is-selected{background:#f58220;color:#fff}.pt-calendar-day i{position:absolute;right:5px;bottom:4px;min-width:5px;height:5px;border-radius:999px;background:#f58220;font-size:0}.pt-calendar-day.is-selected i{background:#fff}.pt-calendar-note{display:flex;align-items:center;gap:7px;margin-top:13px;color:#91a4b3;font-size:10px}.pt-calendar-note i{width:6px;height:6px;border-radius:50%;background:#f58220}.pt-delivery-card{font-family:inherit}
@media(max-width:560px){.pt-calendar{padding:12px}.pt-calendar-day{min-height:36px}.pt-date-picker strong{font-size:16px}}

.pt-delivery-card-meta{display:grid;align-items:flex-start;gap:10px}
.pt-delivery-card-meta .pt-location-summary{width:100%;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.pt-delivery-card-meta .pt-cargo-summary{display:flex;align-items:center;gap:8px;width:100%;min-width:0;white-space:normal}
.pt-cargo-name-summary{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#254d6c;font-weight:750}
.pt-cargo-quantity{flex:none;white-space:nowrap;color:#59758b;font-size:11px;font-weight:750}
.pt-direction-tag{display:inline-flex;align-items:center;width:max-content;margin-bottom:6px;padding:4px 8px;border-radius:999px;font-size:10px;font-weight:850;letter-spacing:.02em}
.pt-direction-tag.pickup{background:#fff4e8;color:#c46a14}
.pt-direction-tag.delivery{background:#edf5ff;color:#286aa2}
.pt-direction-tag.mixed{background:#f1effb;color:#6655a4}
.pt-progress-header{height:88px;display:flex;align-items:center;justify-content:space-between;gap:12px}
.pt-progress-brand{display:flex;align-items:center;gap:10px;min-width:0}
.pt-progress-brand .pt-logo{width:158px}
.pt-back-button{display:grid;place-items:center;flex:none;width:40px;height:40px;border:0;border-radius:13px;background:#f0f6fa;color:#164a73;font:400 31px/1 Inter,"Noto Sans TC",sans-serif;cursor:pointer}
.pt-back-button:hover{background:#e6f0f6}
.pt-progress-title{margin:10px 0 22px}
.pt-progress-title h1{margin:7px 0 9px;font-size:31px;line-height:1.25;letter-spacing:-.035em;overflow-wrap:anywhere}
.pt-progress-title p{margin:0;color:#718899;font-size:13px;font-weight:650}
.pt-progress-overview{padding:18px 19px;border:1px solid #e4edf3;border-radius:17px;background:linear-gradient(135deg,#f3f9fd,#fff);box-shadow:0 8px 22px rgba(23,65,98,.04)}
.pt-progress-overview-top{display:flex;align-items:flex-end;justify-content:space-between;gap:12px;margin-bottom:13px}
.pt-progress-overview-top>div{display:grid;gap:4px}
.pt-progress-overview-top small{color:#8398a8;font-size:10px;font-weight:800}
.pt-progress-overview-top strong{font-size:16px;color:#123d63}
.pt-progress-overview-top>span{color:#617b8f;font-size:11px;font-weight:800;white-space:nowrap}
.pt-progress-meter{height:8px;overflow:hidden;border-radius:999px;background:#e6eef3}
.pt-progress-meter>span{display:block;height:100%;border-radius:inherit;background:linear-gradient(90deg,#f58220,#25a36f);transition:width .25s ease}
.pt-route-section{margin-top:28px}
.pt-route-heading{display:flex;align-items:baseline;justify-content:space-between;gap:12px;margin:0 2px 14px}
.pt-route-heading h2{margin:0;font-size:20px;letter-spacing:-.02em}
.pt-route-heading>span{color:#8398a8;font-size:11px;font-weight:750;white-space:nowrap}
.pt-route-list{display:grid;gap:0}
.pt-route-item{display:grid;grid-template-columns:38px minmax(0,1fr);gap:12px;min-width:0}
.pt-route-rail{display:flex;flex-direction:column;align-items:center;min-height:100%}
.pt-route-marker{z-index:1;display:grid;place-items:center;flex:none;width:34px;height:34px;border-radius:12px;background:#edf5fb;color:#164a73;font-size:11px;font-weight:950}
.pt-route-rail>i{width:2px;flex:1;min-height:18px;margin:5px 0;background:#dfe9ef}
.pt-route-item.is-complete .pt-route-marker{background:#eaf8f0;color:#198754}
.pt-route-item.is-active .pt-route-marker{background:#fff4e8;color:#d97706}
.pt-route-card{min-width:0;margin-bottom:14px;padding:16px 15px;border:1px solid #e1ebf2;border-radius:16px;background:#fff;box-shadow:0 7px 18px rgba(23,65,98,.035)}
.pt-route-item.is-active .pt-route-card{border-color:#f2d5b7}
.pt-route-card-head{display:flex;align-items:flex-start;justify-content:space-between;gap:10px}
.pt-route-card-head h3{min-width:0;margin:1px 0 0;color:#123d63;font-size:15px;line-height:1.55;overflow-wrap:anywhere}
.pt-route-status{display:inline-flex;align-items:center;gap:6px;flex:none;padding:5px 8px;border-radius:999px;font-size:10px;font-weight:850;white-space:nowrap}
.pt-route-status i{width:6px;height:6px;border-radius:50%;background:currentColor}
.pt-route-status.green{background:#eaf8f0;color:#198754}
.pt-route-status.orange{background:#fff4e8;color:#d97706}
.pt-route-status.blue{background:#edf5ff;color:#276aa5}
.pt-route-status.red{background:#fff0f0;color:#c74646}
.pt-progress-cargo-list{display:grid;gap:10px;margin-top:14px;padding-top:12px;border-top:1px solid #edf2f5}
.pt-progress-cargo-row{display:grid;grid-template-columns:minmax(0,1fr) auto auto;align-items:center;gap:9px;min-width:0;color:#3b5d76;font-size:12px}
.pt-progress-cargo-name{min-width:0;font-weight:750;overflow-wrap:anywhere}
.pt-progress-cargo-row strong{color:#123d63;font-size:12px;white-space:nowrap}
.pt-progress-cargo-row em{padding:4px 7px;border-radius:999px;font-size:9px;font-style:normal;font-weight:850;white-space:nowrap}
.pt-progress-cargo-row em.green{background:#eaf8f0;color:#198754}
.pt-progress-cargo-row em.orange{background:#fff4e8;color:#d97706}
.pt-progress-cargo-row em.blue{background:#edf5ff;color:#276aa5}
.pt-progress-cargo-row em.red{background:#fff0f0;color:#c74646}
.pt-no-cargo{margin:0;color:#91a4b3;font-size:12px;line-height:1.6}
@media(max-width:560px){.pt-progress-shell{padding:0 16px 44px}.pt-progress-header{height:78px}.pt-progress-brand .pt-logo{width:133px}.pt-back-button{width:36px;height:36px;font-size:28px}.pt-progress-title h1{font-size:27px}.pt-progress-overview{padding:16px}.pt-route-item{grid-template-columns:32px minmax(0,1fr);gap:9px}.pt-route-marker{width:30px;height:30px;border-radius:10px}.pt-route-card{padding:14px 12px}.pt-route-card-head{display:grid;gap:9px}.pt-route-status{width:max-content}.pt-progress-cargo-row{grid-template-columns:minmax(0,1fr) auto;gap:5px 8px}.pt-progress-cargo-row em{grid-column:2;justify-self:end}}


.pt-station-evidence{margin-top:14px;padding-top:12px;border-top:1px solid #edf2f5}
.pt-evidence-button{display:flex;align-items:center;justify-content:center;width:100%;min-height:42px;padding:9px 12px;border:1px solid #dce9f1;border-radius:11px;background:#f5f9fc;color:#245a7f;font:800 12px Inter,"Noto Sans TC",sans-serif;cursor:pointer}
.pt-evidence-button:hover{border-color:#b9d5e7;background:#edf6fc}
.pt-evidence-button:disabled{opacity:.62;cursor:wait}
.pt-evidence-detail{display:grid;gap:14px;margin-top:13px}
.pt-evidence-media-list,.pt-evidence-video-list{display:flex;flex-wrap:wrap;gap:10px;margin-top:8px}
.pt-evidence-media,.pt-signature-card,.pt-evidence-video{margin:0;min-width:0}
.pt-evidence-media img{display:block;width:112px;height:92px;object-fit:cover;border:1px solid #e1ebf2;border-radius:10px;background:#f3f7fa}
.pt-evidence-media figcaption,.pt-signature-card figcaption,.pt-evidence-video figcaption{margin-top:5px;color:#8295a5;font-size:10px;line-height:1.5}
.pt-evidence-video video{display:block;width:min(100%,360px);max-height:240px;border-radius:11px;background:#0f172a}
.pt-signature-card{display:flex;align-items:center;gap:12px;padding:11px;border:1px solid #e4edf3;border-radius:12px;background:#fbfdfe}
.pt-signature-card img{display:block;width:128px;height:78px;object-fit:contain;border:1px solid #e1ebf2;border-radius:8px;background:#fff}
.pt-signature-card figcaption{margin:0;color:#4e6b80;font-weight:750}
.pt-station-route{min-width:0;padding:12px;border:1px solid #e4edf3;border-radius:13px;background:#fbfdfe}
.pt-station-route-head{display:flex;justify-content:space-between;gap:10px;color:#244e6d;font-size:12px}
.pt-station-route-head span{color:#8295a5;font-size:10px;white-space:nowrap}
.pt-evidence-map{position:relative;width:100%;height:300px;margin-top:10px;overflow:hidden;border:1px solid #dce7ee;border-radius:12px;background:#edf3f6}
.pt-evidence-map .leaflet-container{z-index:0;font-family:Inter,"Noto Sans TC",sans-serif}
.pt-evidence-map .leaflet-top.leaflet-left{top:10px;left:10px;z-index:800}
.pt-evidence-map .leaflet-control-zoom{margin:0;border:0;border-radius:11px;overflow:hidden;background:rgba(255,255,255,.78);box-shadow:0 6px 16px rgba(15,23,42,.14);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px)}
.pt-evidence-map .leaflet-control-zoom a{width:32px;height:30px;border:0!important;border-bottom:1px solid rgba(148,163,184,.2)!important;background:rgba(255,255,255,.58);color:#334155;font-size:17px;font-weight:900;line-height:30px;transition:background .16s,color .16s}
.pt-evidence-map .leaflet-control-zoom a:last-child{border-bottom:0!important}
.pt-evidence-map .leaflet-control-zoom a:hover{background:rgba(255,255,255,.9);color:#e9781e}
.pt-evidence-map .leaflet-control-attribution{box-sizing:border-box;max-width:calc(100% - 20px);margin:0 8px 8px 0!important;padding:4px 9px!important;border:1px solid rgba(255,255,255,.82);border-radius:999px;background:rgba(255,255,255,.78);box-shadow:0 3px 10px rgba(15,23,42,.12);color:#64748b;font-size:9px;font-weight:700;line-height:1.25;white-space:nowrap;backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px)}
.pt-evidence-map .leaflet-control-attribution a{color:#475569!important;font-weight:800;text-decoration:none}
.pt-evidence-map .leaflet-control-attribution a:hover{color:#e9781e!important;text-decoration:underline}
.pt-map-legend{position:absolute;left:10px;bottom:10px;z-index:500;display:flex;align-items:center;gap:8px;padding:6px 9px;border:1px solid rgba(255,255,255,.85);border-radius:999px;background:rgba(255,255,255,.86);box-shadow:0 3px 10px rgba(15,23,42,.12);color:#475569;font-size:9px;font-weight:800;pointer-events:none;backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px)}
.pt-map-legend span{display:flex;align-items:center;gap:4px;white-space:nowrap}
.pt-map-legend i{width:8px;height:8px;border:1px solid #fff;border-radius:50%;box-shadow:0 0 0 1px rgba(15,23,42,.14)}
.pt-map-legend i.is-start{background:#18b977}
.pt-map-legend i.is-end{background:#ef5360}
.pt-map-legend>b{width:28px;height:4px;border-radius:99px;background:linear-gradient(90deg,#18b977,#ef5360)}
@media(max-width:560px){.pt-evidence-media img{width:92px;height:78px}.pt-evidence-map{height:245px}.pt-signature-card img{width:108px;height:68px}.pt-evidence-button{font-size:11px}}

.pt-progress-summary{display:grid;grid-template-columns:1fr 1fr;margin:22px 0 30px;padding:4px 0}
.pt-progress-stat{display:grid;align-content:start;gap:4px;min-width:0;padding:0 16px 0 0}
.pt-progress-stat+.pt-progress-stat{padding:0 0 0 18px;border-left:1px solid #e4edf3}
.pt-progress-stat small{color:#718899;font-size:12px;font-weight:750}
.pt-progress-stat strong{color:#123d63;font-size:22px;line-height:1.25;overflow-wrap:anywhere}
.pt-progress-stat span{color:#718899;font-size:12px;overflow-wrap:anywhere}
.pt-route-section{margin-top:20px}
.pt-route-heading{margin-bottom:12px}
.pt-route-heading h2{font-size:21px}
.pt-route-item{grid-template-columns:38px minmax(0,1fr);gap:12px}
.pt-route-card{padding:0;overflow:hidden;border-radius:17px}
.pt-station-toggle{display:flex;align-items:center;justify-content:space-between;gap:12px;width:100%;min-height:86px;padding:15px 16px;border:0;background:#fff;color:#123d63;text-align:left;font:inherit;cursor:pointer}
.pt-station-toggle:hover{background:#fbfdfe}
.pt-station-toggle-main{display:grid;gap:4px;min-width:0}
.pt-station-sequence{color:#8398a8;font-size:10px;font-weight:850;letter-spacing:.04em}
.pt-station-toggle-main>strong{color:#123d63;font-size:16px;line-height:1.45;overflow-wrap:anywhere}
.pt-station-toggle-main>small{color:#8295a5;font-size:10px;line-height:1.5}
.pt-station-toggle-side{display:flex;align-items:center;gap:9px;flex:none}
.pt-station-chevron{display:inline-block;color:#758b9c;font-size:22px;line-height:1;transition:transform .18s ease}
.pt-station-chevron.is-open{transform:rotate(180deg)}
.pt-station-body{padding:0 16px 16px}
.pt-cargo-section{padding-top:14px;border-top:1px solid #edf2f5}
.pt-station-subheading{display:flex;align-items:center;gap:9px;min-width:0;color:#164a73}
.pt-station-subheading h4{flex:1;min-width:0;margin:0;color:#244e6d;font-size:13px;font-weight:850}
.pt-station-subheading>span{color:#8295a5;font-size:10px;font-weight:750;white-space:nowrap}
.pt-progress-cargo-list{display:grid;gap:9px;margin-top:11px;padding:0;border:0}
.pt-progress-cargo-card{min-width:0;padding:12px;border:1px solid #e8eff3;border-radius:12px;background:#fbfdfe}
.pt-cargo-card-head{display:flex;align-items:center;justify-content:space-between;gap:10px}
.pt-cargo-card-head>div{display:grid;gap:3px;min-width:0}
.pt-cargo-card-head>div>small{color:#8295a5;font-size:9px;font-weight:750}
.pt-progress-cargo-name{color:#123d63;font-size:14px;font-weight:850}
.pt-cargo-quantity-large{flex:none;color:#123d63;font-size:17px;white-space:nowrap}
.pt-cargo-quantity-large small{color:#718899;font-size:11px;font-weight:700}
.pt-cargo-card-meta{display:flex;align-items:center;justify-content:space-between;gap:9px;margin-top:8px}
.pt-cargo-card-meta em{padding:4px 8px;border-radius:999px;background:#eaf8f0;color:#198754;font-size:10px;font-style:normal;font-weight:850}
.pt-cargo-card-meta em.orange{background:#fff4e8;color:#d97706}
.pt-cargo-card-meta em.blue{background:#edf5ff;color:#276aa5}
.pt-cargo-card-meta em.red{background:#fff0f0;color:#c74646}
.pt-cargo-card-meta>small{color:#8295a5;font-size:10px;text-align:right}
.pt-cargo-photo-block{margin-top:11px}
.pt-cargo-photo-block>span{display:block;margin-bottom:6px;color:#8295a5;font-size:10px;font-weight:750}
.pt-cargo-photo-list{display:flex;gap:8px;overflow-x:auto;padding:0 0 3px}
.pt-cargo-photo-list a{display:block;flex:none}
.pt-cargo-photo{width:82px;height:72px;border-radius:9px}
.pt-station-evidence{margin-top:14px;padding-top:14px}
.pt-evidence-button{min-height:44px;background:#f3f8fc;font-size:12px}
.pt-evidence-detail{gap:14px;margin-top:14px}
.pt-station-worklog,.pt-station-media-panel{min-width:0;padding:13px;border:1px solid #e8eff3;border-radius:13px;background:#fff}
.pt-station-timeline{display:grid;gap:0;margin:12px 0 0;padding:0;list-style:none}
.pt-timeline-event{position:relative;display:grid;grid-template-columns:46px 14px minmax(0,1fr);gap:9px;min-width:0;min-height:38px}
.pt-timeline-event>time{padding-top:2px;color:#8295a5;font-size:10px;font-variant-numeric:tabular-nums}
.pt-timeline-dot{position:relative;z-index:1;width:10px;height:10px;margin:4px auto 0;border:2px solid #fff;border-radius:50%;background:#b8c6d0;box-shadow:0 0 0 1px #dbe5eb}
.pt-timeline-event:not(:last-child)::after{position:absolute;top:13px;bottom:-1px;left:61px;width:1px;background:#e3ebf0;content:""}
.pt-timeline-event.is-signature .pt-timeline-dot{background:#f58220;box-shadow:0 0 0 1px #f7c28e}
.pt-timeline-content{display:grid;align-content:start;gap:6px;min-width:0;padding-bottom:12px;color:#244e6d}
.pt-timeline-content>strong{font-size:12px;font-weight:850}
.pt-timeline-note{color:#8295a5;font-size:10px;line-height:1.5}
.pt-signature-card{display:flex;align-items:center;gap:9px;padding:7px;border:1px solid #e6edf1;border-radius:10px;background:#fbfdfe}
.pt-signature-card img{width:102px;height:62px}
.pt-signature-card figcaption{color:#4e6b80;font-size:9px}
.pt-station-media-gallery{display:flex;gap:10px;overflow-x:auto;margin-top:10px;padding-bottom:4px}
.pt-evidence-media{flex:none;width:124px}
.pt-evidence-media img{width:124px;height:94px;border-radius:10px}
.pt-evidence-media figcaption{max-width:124px;font-size:9px}
.pt-evidence-video{flex:none;width:min(76vw,280px)}
.pt-evidence-video video{width:100%;max-height:190px}
.pt-evidence-video figcaption{font-size:9px}
.pt-station-route{padding:0 12px 12px;border-radius:13px;background:#fbfdfe}
.pt-station-route-head{min-height:44px;align-items:center;cursor:pointer;list-style:none}
.pt-station-route-head::-webkit-details-marker{display:none}
.pt-station-route-head::after{color:#8295a5;font-size:20px;content:"⌄"}
.pt-station-route[open]>.pt-station-route-head::after{transform:rotate(180deg)}
.pt-station-route-head strong{font-size:12px}
.pt-station-route>.pt-stop-execution-times{margin-top:0}
.pt-completion-footer{display:inline-block;padding-top:24px;color:#49677d;font-weight:850}
@media(max-width:560px){
  .pt-progress-summary{margin:18px 0 25px}
  .pt-progress-stat{padding-right:11px}
  .pt-progress-stat+.pt-progress-stat{padding-left:12px}
  .pt-progress-stat strong{font-size:19px}
  .pt-route-item{grid-template-columns:30px minmax(0,1fr);gap:9px}
  .pt-station-toggle{min-height:78px;padding:12px 11px}
  .pt-station-toggle-main>strong{font-size:14px}
  .pt-station-toggle-side{gap:6px}
  .pt-station-toggle-side .pt-route-status{font-size:9px;padding:5px 7px}
  .pt-station-body{padding:0 11px 12px}
  .pt-progress-cargo-card{padding:10px}
  .pt-station-worklog,.pt-station-media-panel{padding:11px}
  .pt-timeline-event{grid-template-columns:40px 12px minmax(0,1fr);gap:7px}
  .pt-timeline-event:not(:last-child)::after{left:53px}
  .pt-evidence-media,.pt-evidence-media img{width:108px}
  .pt-evidence-media img{height:82px}
}

.pt-progress-shell{width:min(100%,1240px);padding-left:clamp(16px,3vw,40px);padding-right:clamp(16px,3vw,40px)}
.pt-image-thumb{display:block;min-width:0;padding:0;border:0;background:transparent;text-align:left;cursor:zoom-in}
.pt-image-thumb:focus-visible{outline:3px solid rgba(245,130,32,.65);outline-offset:3px;border-radius:10px}
.pt-progress-shell .pt-signature-card{background:#eef1f3}
.pt-progress-shell .pt-signature-card img{background:#e7ebee}
.pt-progress-shell .pt-evidence-media{padding:6px;border-radius:12px;background:#eef1f3}
.pt-progress-shell .pt-evidence-media img,.pt-progress-shell .pt-cargo-photo{background:#e7ebee}
.pt-cargo-photo-list{display:grid;grid-template-columns:repeat(auto-fill,minmax(72px,92px));gap:8px;overflow:visible;padding:0}
.pt-cargo-photo-list .pt-image-thumb{width:100%}
.pt-cargo-photo{width:100%;height:auto;aspect-ratio:1/1;object-fit:cover}
.pt-station-media-gallery{display:grid;grid-template-columns:repeat(auto-fill,minmax(min(100%,150px),1fr));gap:12px;overflow:visible;margin-top:10px;padding:0}
.pt-evidence-media,.pt-evidence-video{width:auto;min-width:0}
.pt-evidence-media img{width:100%;height:auto;aspect-ratio:4/3;object-fit:cover}
.pt-evidence-media figcaption{max-width:none}
.pt-evidence-video video{display:block;width:100%;max-height:260px;border-radius:10px;background:#111}
.pt-image-lightbox{position:fixed;inset:0;z-index:10000;display:grid;place-items:center;padding:56px 24px 28px;background:rgba(225,229,233,.97);backdrop-filter:blur(5px)}
.pt-image-lightbox-close{position:absolute;top:16px;right:18px;display:grid;place-items:center;width:44px;height:44px;border:1px solid rgba(80,94,105,.25);border-radius:50%;background:rgba(77,91,103,.82);color:#fff;font-size:32px;line-height:1;cursor:pointer}
.pt-image-lightbox-content{display:grid;justify-items:center;gap:12px;width:100%;height:100%;min-height:0}
.pt-image-lightbox-content img{display:block;width:auto;height:auto;max-width:100%;max-height:calc(100vh - 118px);object-fit:contain;border:1px solid #cbd2d8;border-radius:6px;background:#e7ebee;box-shadow:0 12px 35px rgba(40,52,62,.16)}
.pt-image-lightbox-content p{margin:0;color:#526271;font-size:12px;text-align:center}
.pt-progress-header-actions{display:flex;align-items:center;justify-content:flex-end;gap:9px;flex-wrap:wrap}
.pt-pdf-button{min-height:38px;padding:8px 12px;border:1px solid #dce9f1;border-radius:11px;background:#fff;color:#245a7f;font:800 11px Inter,"Noto Sans TC",sans-serif;white-space:nowrap;cursor:pointer}
.pt-pdf-button:hover{border-color:#f4bd8c;background:#fff8f2;color:#c66c1f}
.pt-pdf-button:disabled{opacity:.65;cursor:wait}
.pt-export-error{margin:0 0 12px;color:#bd3f3f;font-size:12px}
@media(min-width:1600px){.pt-progress-shell{width:min(100%,1360px);max-width:1360px}}
@media(min-width:561px) and (max-width:900px){.pt-progress-shell{padding-left:22px;padding-right:22px}.pt-progress-summary{gap:16px}.pt-route-item{grid-template-columns:34px minmax(0,1fr);gap:10px}.pt-evidence-detail{grid-template-columns:minmax(0,1fr)}}
@media(max-width:560px){.pt-progress-shell{width:100%;padding-left:14px;padding-right:14px}.pt-progress-header{height:auto;min-height:78px}.pt-progress-header-actions{flex-direction:column;align-items:flex-end;gap:5px}.pt-pdf-button{min-height:30px;padding:5px 8px;font-size:10px}.pt-progress-title h1{font-size:clamp(21px,6vw,27px);overflow-wrap:anywhere}.pt-progress-summary{grid-template-columns:repeat(2,minmax(0,1fr));gap:0}.pt-progress-stat{min-width:0}.pt-progress-stat strong{font-size:clamp(16px,4.8vw,20px);overflow-wrap:anywhere}.pt-route-heading h2{font-size:18px}.pt-route-item{grid-template-columns:27px minmax(0,1fr);gap:7px}.pt-route-card{padding:11px 9px}.pt-station-toggle{gap:7px}.pt-station-toggle-main>strong{font-size:13px}.pt-station-toggle-side .pt-route-status{max-width:90px;white-space:normal;text-align:center}.pt-station-body{padding:0 9px 10px}.pt-cargo-card-head{grid-template-columns:minmax(0,1fr) auto}.pt-cargo-quantity-large{font-size:15px}.pt-cargo-photo-list{grid-template-columns:repeat(auto-fill,minmax(64px,1fr))}.pt-station-media-gallery{grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}.pt-evidence-media figcaption{font-size:9px}.pt-timeline-event{grid-template-columns:38px 12px minmax(0,1fr);gap:6px}.pt-timeline-event:not(:last-child)::after{left:51px}.pt-image-lightbox{padding:54px 12px 20px}.pt-image-lightbox-close{top:10px;right:10px;width:40px;height:40px}}
@media print{
  @page{margin:12mm}
  html,body{background:#fff!important;color:#193b54!important;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .pt-page{min-height:0!important;background:#fff!important}
  .pt-progress-shell{width:100%!important;max-width:none!important;margin:0!important;padding:0!important;box-shadow:none!important}
  .pt-back-button,.pt-pdf-button,.pt-evidence-button,.pt-image-lightbox,.pt-station-chevron{display:none!important}
  .pt-progress-header{height:auto;min-height:0;margin-bottom:8mm}
  .pt-status-chip{print-color-adjust:exact;-webkit-print-color-adjust:exact}
  .pt-station-body{display:block!important;padding-bottom:5mm}
  .pt-station-toggle{min-height:0;padding:4mm 3mm;pointer-events:none}
  .pt-route-card,.pt-progress-cargo-card,.pt-station-worklog,.pt-station-media-panel,.pt-station-route{overflow:visible!important;box-shadow:none!important;break-inside:avoid-page}
  .pt-route-item{break-inside:avoid-page}
  .pt-evidence-detail{grid-template-columns:minmax(0,1fr)}
  .pt-cargo-photo-list{grid-template-columns:repeat(5,minmax(0,1fr));overflow:visible}
  .pt-cargo-photo-list .pt-image-thumb{max-width:none}
  .pt-station-media-gallery{grid-template-columns:repeat(3,minmax(0,1fr));overflow:visible}
  .pt-evidence-media,.pt-evidence-video{break-inside:avoid-page}
  .pt-evidence-media img{max-height:55mm;object-fit:contain}
  .pt-evidence-video video{display:none}
  .pt-evidence-video::before{display:grid;place-items:center;min-height:34mm;border:1px dashed #cbd8e0;border-radius:10px;background:#f5f8fa;color:#5d7485;font-size:10pt;content:"影片請於線上追蹤頁播放"}
  .pt-signature-card img{width:45mm;height:auto;max-height:30mm;object-fit:contain}
  .pt-image-thumb{cursor:default}
  .pt-station-route-head{cursor:default}
  .pt-evidence-map{height:70mm;break-inside:avoid-page}
  .pt-evidence-map .leaflet-top.leaflet-left{top:7px;left:7px}
  .pt-evidence-map .leaflet-control-attribution{margin:0 5px 5px 0!important;padding:3px 7px!important;font-size:7px}
  .leaflet-control-zoom{display:none!important}
}

`;

export default function PublicTrackingPageWithStyles() {
  return <div className="pt-page"><PublicTrackingPage /><style>{publicTrackingStyles}</style></div>;
}
